import LegalLayout from "../../components/legal/LegalLayout";
import { useSettings } from "../../features/settings/api";
import useSEO from "../../hooks/useSEO";

// Generic template — legally accurate to how this store actually works
// (no payment processor, no ad-tech tracking) but the bracketed fields
// need the business's real legal name / registered address / governing
// jurisdiction filled in before this is a binding policy. Not legal
// advice; have it reviewed before relying on it in production.
export default function Privacy() {
  useSEO({ title: "Privacy Policy" });
  const { data: settings } = useSettings();
  const contactEmail = settings?.contactEmail || "[business contact email]";

  return (
    <LegalLayout title="Privacy Policy" updated="12 August 2026">
      <p>
        This policy explains what information Smart Connect Electrical ("we," "us") collects when
        you use this website, and how it's used. It applies to visitors, registered account
        holders, and anyone who submits an order request.
      </p>

      <Section title="Information we collect">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>
            <strong>Account details</strong> — name, email, and password (stored as a salted hash,
            never in plain text) if you create an account; phone number if you provide one.
          </li>
          <li>
            <strong>Order request details</strong> — name, phone number, delivery address, and any
            notes you submit at checkout, whether or not you're signed in.
          </li>
          <li>
            <strong>Reviews</strong> — the name, rating, and comment you submit for a product.
          </li>
          <li>
            <strong>Local device storage</strong> — your cart, wishlist (if browsing as a guest),
            and light/dark theme preference are stored in your browser's local storage, not on our
            servers, until you sign in (see "Wishlist syncing" below).
          </li>
          <li>
            <strong>Session cookies</strong> — a signed-in session uses httpOnly cookies to keep you
            logged in; these aren't readable by page scripts and aren't used for advertising or
            cross-site tracking.
          </li>
        </ul>
      </Section>

      <Section title="What we don't collect">
        <p>
          We never process or store payment card details — this store doesn't use a payment
          gateway. Every order is a request that a human confirms with you directly (by WhatsApp
          or phone) before any payment is arranged. We also don't use third-party advertising or
          analytics trackers.
        </p>
      </Section>

      <Section title="How we use your information">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>To follow up on and fulfill an order request you submit.</li>
          <li>To maintain your account and order history if you're signed in.</li>
          <li>To moderate and (if approved) publish product reviews.</li>
          <li>To respond if you contact us directly.</li>
        </ul>
        <p className="mt-2">We do not sell or rent your information to third parties.</p>
      </Section>

      <Section title="Wishlist syncing">
        <p>
          If you save items to your wishlist as a guest and later create an account or sign in,
          those items are uploaded to your account so they're available on any device. Signed-out
          browsing keeps your wishlist local to that device only.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          You can review or update your account details at any time from the Account page. To
          request a copy of your data, or to have your account and associated data deleted, contact
          us at <a className="text-gold-600 underline dark:text-gold-400" href={`mailto:${contactEmail}`}>{contactEmail}</a>.
        </p>
      </Section>

      <Section title="Children's privacy">
        <p>This site is not directed at children under 16, and we don't knowingly collect their data.</p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          If this policy changes materially, we'll update the date at the top of this page.
        </p>
      </Section>

      <Section title="Governing law">
        <p>
          This policy is governed by the laws of{" "}
          <span className="rounded bg-mono/[0.06] px-1 py-0.5 font-mono text-xs">
            [Governing jurisdiction — e.g. state/country of business registration]
          </span>.
        </p>
      </Section>

      <p className="text-xs text-mono/40">
        This is a template, not legal advice. Confirm it matches your actual practices and local
        regulations (e.g. GDPR, CCPA) before relying on it, and fill in the business's registered
        legal name, address, and jurisdiction where marked.
      </p>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="mb-1.5 font-serif text-base text-ink">{title}</h2>
      {children}
    </section>
  );
}
